<script setup lang="ts">
import { computed } from "vue";
import { Heading } from "~/components/clarity-ui/Heading";
import { Paragraph } from "~/components/clarity-ui/Paragraph";
import useCommonLabels from "~/composables/useCommonLabels";
import SvgIcon from "~/templates/elements/SvgIcon.vue";
import VideoPictureElement from "~/templates/elements/VideoPictureElement.vue";
import { DIMENSIONS } from "~/utils/helper/imaging";
import { contentToTailwindClasses } from "~/utils/helper/tailwind";
import { hasVideo } from "~/utils/helper/video";

export interface ImageComponentProps {
  image?: object;
  video?: object;
  showVideoInline?: boolean;
  text?: string;
  opacity?: number;
  position?: string;
  headline?: {
    text: string;
  };
  subline?: string;
}

defineProps<ImageComponentProps>();

const { isDesktop } = useDevice();

const BREAKPOINTS = {
  start: DIMENSIONS["start"].ratio["2/3"],
  sm: DIMENSIONS["sm"].ratio.video,
  md: DIMENSIONS["md"].ratio.video,
  xl: DIMENSIONS["xl"].ratio.video,
  container: DIMENSIONS["container"].ratio.video,
};

const { getLabelByPath } = useCommonLabels();
const playButtonAriaLabel = computed(() => getLabelByPath("BWF/shared/videoPlayButtonAriaLabel"));
</script>

<template>
  <div class="image-component responsive-gap flex w-full flex-col">
    <VideoPictureElement
      :breakpoints="BREAKPOINTS"
      :img="image"
      :show-video-inline="showVideoInline === true || isDesktop"
      :video="video"
      class="image-rounded relative mx-auto aspect-2/3 max-h-screen w-full overflow-hidden sm:aspect-video"
      img-class="absolute inset-0 w-full h-full object-cover"
    >
      <div class="relative inset-0 h-full w-full">
        <div
          v-if="opacity > 0"
          :class="contentToTailwindClasses({ opacity: opacity ?? 0 })"
          class="absolute inset-0 bg-black"
        />
        <div
          :class="{
            'items-center justify-center text-center': position === 'center',
            'items-start justify-end': position !== 'center',
          }"
          class="container relative inset-0 flex h-full w-full flex-col gap-4 p-8 xl:gap-8"
        >
          <div
            v-if="text"
            class="w-10/12 text-xl font-medium text-white md:text-2xl lg:w-1/2 lg:text-4xl"
          >
            {{ text }}
          </div>
          <button
            v-if="hasVideo(video)"
            class="btn btn-circle h-16 w-16 bg-white xl:h-24 xl:w-24"
            :aria-label="playButtonAriaLabel"
          >
            <SvgIcon class="h-6 w-6 text-black xl:h-8 xl:w-8" name="Play" />
          </button>
        </div>
      </div>
    </VideoPictureElement>
    <div v-if="subline || headline?.text" class="no-nested-container-padding container">
      <Heading v-if="headline?.text" size="medium">{{ headline?.text }}</Heading>
      <Paragraph v-if="subline" class="mt-2 xl:mt-4">{{ subline }}</Paragraph>
    </div>
  </div>
</template>

<style>
.image-component .image-rounded {
  @apply rounded-xl;
}

.no-container .image-rounded {
  @apply rounded-none;
}
</style>
